<template>
  <div class="mt-4 min-h-[400px]">
    <p class="font-bold text-xl tracking-tight text-gray-900 m-0 pb-2">
      Informations de paiement
      <span class="ml-1" v-if="offerPrice?.monthly">• {{ formatPrice(offerPrice.price) }} / mois</span>
    </p>
    <div v-if="offerPrice" class="mt-2">
      <form id="payment-form" @submit.prevent="emitConfirmationRequest">
        <div ref="paymentElementContainer" id="payment-element"></div>
        <p class="mt-2 text-xs text-gray-500">
          En saisissant vos informations de paiement, vous autorisez Estaly à prélever les primes liées à votre contrat.
        </p>
        <p class="mt-2 text-xs text-red-400">
          Pour les paiements en SEPA (via RIB), la validation peut prendre entre 3 à 6 jours ouvrés selon les délais
          bancaires.
        </p>
        <div id="payment-message" class="hidden"></div>
        <div class="flex flex-col justify-center items-center space-y-4">
          <div class="mt-8">
            <PrimaryButton :loading="isSendingConsentCode" @click="$emit('sendConsentCode')">
              Envoyer le code de confirmation
            </PrimaryButton>
          </div>
          <div>
            <SecondaryButton v-if="isFailedPayment" @click="$emit('sendFailedPaymentLink')">
              Envoyer un lien de paiement au client
            </SecondaryButton>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { PrimaryButton, SecondaryButton, isPresent } from "@estaly/ui";
import { loadStripe } from "@stripe/stripe-js";
import { mapActions } from "vuex";
import { formatPrice } from "@estaly/ui";
export default {
  components: {
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    offerPrice: Object,
    discount: Object,
    isSendingConsentCode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isPromotionOnMonths() {
      if (isPresent(this.discount?.type)) {
        return this.discount.type.split("_")[0] === "months" && this.offerPrice.recurring;
      } else {
        return false;
      }
    },
    isOffered() {
      if (isPresent(this.discount?.type)) {
        return this.discount.type === "months_offered" && this.offerPrice.recurring;
      } else {
        return false;
      }
    },
  },
  data() {
    return {
      stripeKey: `${process.env.VUE_APP_STRIPE_KEY}`,
      isFailedPayment: false,
      selectedPaymentType: null,
    };
  },
  watch: {
    offerPrice: function (newVal, oldVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.initialize();
        this.checkStatus();
        const form = document.querySelector("#payment-form");
        if (form) form.addEventListener("submit", this.handleSubmit);
        this.emailAddress = "";
      }
    },
  },
  async mounted() {
    this.stripe = await loadStripe(this.stripeKey, { locale: "fr" });
    await this.$nextTick();
    await this.initialize();
    this.checkStatus();
    const form = document.querySelector("#payment-form");
    if (form) form.addEventListener("submit", this.handleSubmit);
    this.emailAddress = "";
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    formatPrice,

    emitConfirmationRequest() {
      this.$emit("requestConfirmationCode");
    },

    async initialize() {
      if (!this.offerPrice?.stripeClient || !this.stripe) return;

      await this.$nextTick();
      const mountTarget = this.$refs.paymentElementContainer;
      if (!mountTarget) return;

      const clientSecret = this.offerPrice.stripeClient;
      const appearance = {
        theme: "stripe",
        variables: {
          borderRadius: "1rem",
          fontSizeSm: "14px",
          fontLineHeight: "16px",
          spacingUnit: "3px",
          spacingGridColumn: "1.5rem",
          fontSizeBase: "0.875rem",
        },
        rules: {
          ".Input": {
            border: "1px solid #9ca3af",
          },
          ".Input::placeholder": {
            "font-size": "0.875rem",
          },
        },
      };

      this.elements = this.stripe.elements({ appearance, clientSecret });
      const paymentElementOptions = {
        layout: "tabs",
        terms: {
          card: "never",
          sepaDebit: "never",
        },
      };
      const paymentElement = this.elements.create("payment", paymentElementOptions);

      paymentElement.on("change", (event) => {
        this.selectedPaymentType = event?.value?.type || null;
      });

      paymentElement.mount(mountTarget);
    },

    async handleSubmit() {
      const elements = this.elements;
      let result = null;

      if (this.discount.type === "months_offered" && this.offerPrice.recurring) {
        result = await this.stripe.confirmSetup({
          elements,
          redirect: "if_required",
          confirmParams: {
            setup_intent: this.setup_intent_client_secret,
            return_url: `${window.location.origin}/`,
          },
        });
      } else {
        result = await this.stripe.confirmPayment({
          elements,
          redirect: "if_required",
          confirmParams: {
            receipt_email: this.emailAddress,
            return_url: `${window.location.origin}/`,
          },
        });
      }

      let error = result.error;

      if (
        !error &&
        (result.paymentIntent?.status === "succeeded" ||
          result.paymentIntent?.status === "processing" ||
          result.setupIntent?.status === "succeeded" ||
          result.setupIntent?.status === "processing")
      ) {
        const isSepaPayment = this.selectedPaymentType === "sepa_debit";

        this.$emit("paymentSuccess", {
          success: true,
          matchedContractId: this.offerPrice?.id,
          isSepaPayment: isSepaPayment,
        });
      } else if (error) {
        console.log(error);
        this.isFailedPayment = true;
        this.$emit("showConfirmationStep", false);
        await this.notify({
          category: "simple",
          type: "error",
          title: error.message,
          text: "Réessayez ou envoyez un lien de paiement au client.",
        });
      }
    },

    async validatePaymentDetails() {
      if (!this.elements) {
        await this.initialize();
      }

      const { error } = await this.elements.submit();

      if (error) {
        await this.notify({
          category: "simple",
          type: "error",
          title: "Informations manquantes",
          text: "Complétez les informations de paiement avant de continuer.",
        });
        return false;
      }

      return true;
    },

    async checkStatus() {
      const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
      if (!clientSecret) {
        return;
      }
      const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);
      switch (paymentIntent.status) {
        case "succeeded":
          this.showMessage("Payment succeeded!");
          break;
        case "processing":
          this.showMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          this.showMessage("Your payment was not successful, please try again.");
          break;
        default:
          this.showMessage("Something went wrong.");
          break;
      }
    },

    showMessage(messageText) {
      const messageContainer = document.querySelector("#payment-message");
      messageContainer.classList.remove("hidden");
      messageContainer.textContent = messageText;
      setTimeout(function () {
        messageContainer.classList.add("hidden");
        messageText.textContent = "";
      }, 10000);
    },

    setLoading(isLoading) {
      if (isLoading) {
        // Disable the button and show a spinner
        document.querySelector("#submit").disabled = true;
        document.querySelector("#spinner").classList.remove("hidden");
        document.querySelector("#button-text").classList.add("hidden");
        document.querySelector("#loading").classList.remove("hidden");
      } else {
        document.querySelector("#submit").disabled = false;
        document.querySelector("#spinner").classList.add("hidden");
        document.querySelector("#button-text").classList.remove("hidden");
        document.querySelector("#loading").classList.add("hidden");
      }
    },
  },
};
</script>
